// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionshipAdminRequest,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  ChampionshipAdminRequestStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import ChampionshipAdminRequestFields from '../../../fields/ChampionshipAdminRequestFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';
import type {
  $RedirectAction,
} from '../../../../redux/modules/main/actions';

export type $OwnProps = $Translate & {
  id: string;
  item: $Model<'Championship'>;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Admin'>>;
  dataAdminRequests: Array<$FullChampionshipAdminRequest>;
  loading: boolean;
};

class ChampionshipAdminsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataAdminRequests: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getAdminData();
    this.getAdminRequestData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getAdminData();
      this.getAdminRequestData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.ChampionshipId && data.modelName === 'Admin') {
          this.getAdminData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.ChampionshipId && data.modelName === 'ChampionshipAdminRequest') {
          this.getAdminRequestData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getAdminData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyChampionshipAdmin({
          ChampionshipId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getAdminRequestData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const dataAdminRequests = await Services.Api.getManyChampionshipChampionshipAdminRequest({
          ChampionshipId,
        });

        await this.setStateAsync({
          dataAdminRequests,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const ChampionshipId = item.id;

    const newItem: $ModelUpsertParams<'ChampionshipAdminRequest'> = {
      ChampionshipId,
      Email: '',
      Name: null,
      Status: ChampionshipAdminRequestStatus.request,
    };

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-lock'}
        rightButtons={(
          <FormDialog
            fields={(
              <ChampionshipAdminRequestFields item={newItem} />
            )}
            id={'create-championship-admin-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'ChampionshipAdminRequest'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertChampionshipAdminRequest(params);
              },
            })}
            subTitle={t('New')}
            title={t('Championship admin')}
          >
            <Button
              color={'info'}
              id={'create-championship-admin-button'}
              size={'sm'}
              title={t('Add championship admin')}
            >
              {t('Add')}
            </Button>
          </FormDialog>
        )}
        title={t('Championship admins')}
      />
    );
  }

  renderAdmins() {
    const {
      auth,
      item,
      redirect,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const ChampionshipId = item.id;

    return (
      <>
        {data.map((dataItem: $Model<'Admin'>) => {
          const {
            id,
          } = dataItem;

          return (
            <tr key={id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td className={'text-right'}>
                <DeleteDialog
                  content={t('Do you really want to remove?')}
                  id={`delete-championship-admin-dialog-${id}`}
                  labelCloseButton={t('Close')}
                  labelDeleteButton={t('Delete')}
                  onAction={() => defaultDeleteItem({
                    action: async () => {
                      await Services.Api.removeChampionshipAdmin({
                        AdminId: id,
                        ChampionshipId,
                      });

                      if (dataItem.id === _.get(
                        auth,
                        'user.id',
                      )) {
                        redirect({
                          pathname: '/championships',
                        });
                      }
                    },
                  })}
                  subTitle={_.get(
                    _.find(
                      data,
                      {
                        id,
                      },
                    ),
                    'Name',
                  ) || ''}
                  title={t('Championship admin')}
                >
                  <Button
                    color={'danger'}
                    id={`delete-championship-admin-button-${id}`}
                    outline
                    size={'sm'}
                  >
                    {t('Remove')}
                  </Button>
                </DeleteDialog>
              </td>
            </tr>
          );
        })}
      </>
    );
  }

  renderPendingAdmins() {
    const {
      t,
    } = this.props;
    const {
      dataAdminRequests,
    } = this.state;

    const filteredAdminRequests = _.filter(
      dataAdminRequests,
      ({
        Status,
      }) => Status !== ChampionshipAdminRequestStatus.fulfilled,
    );

    return (filteredAdminRequests.length > 0 ? (
      <>
        <tr>
          <td
            className={'text-center'}
            colSpan={100}
          >
            <h5 className={'mb-0 text-bold'}>
              {t('Championship pending admins')}
            </h5>
          </td>
        </tr>
        {filteredAdminRequests.map((dataItem: $FullChampionshipAdminRequest) => {
          const {
            id,
          } = dataItem;

          return (
            <tr key={id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td className={'text-right'}>
                {ChampionshipAdminRequestStatus.propose === dataItem.Status && (
                  <CommonDialog
                    content={(
                      <CardBody>
                        {t('Do you really want to add this user to admins?')}
                      </CardBody>
                    )}
                    id={`confirm-championship-admin-request-dialog-${id}`}
                    labelCloseButton={t('Close')}
                    labelConfirmButton={t('Confirm')}
                    onAction={() => defaultUpsertItem({
                      action: async () => {
                        const params = cleanFormValues<$FullChampionshipAdminRequest>({
                          ...dataItem,
                          Status: ChampionshipAdminRequestStatus.confirmed,
                        });

                        await Services.Api.upsertChampionshipAdminRequest(params);
                      },
                    })}
                    title={t('Need confirmation')}
                  >
                    <Button
                      color={'warning'}
                      id={`confirm-championship-admin-request-button-${id}`}
                      size={'sm'}
                    >
                      {t('Confirm')}
                    </Button>
                  </CommonDialog>
                )}
                {ChampionshipAdminRequestStatus.request === dataItem.Status && (
                  <DeleteDialog
                    content={t('Do you really want to revoke?')}
                    id={`revoke-championship-admin-request-dialog-${id}`}
                    labelCloseButton={t('Close')}
                    labelDeleteButton={t('Delete')}
                    onAction={() => defaultDeleteItem({
                      action: async () => Services.Api.deleteChampionshipAdminRequest(dataItem),
                    })}
                    subTitle={dataItem.Name || ''}
                    title={t('Championship admin request')}
                  >
                    <Button
                      color={'danger'}
                      id={`revoke-championship-admin-request-button-${id}`}
                      outline
                      size={'sm'}
                    >
                      {t('Revoke')}
                    </Button>
                  </DeleteDialog>
                )}
              </td>
            </tr>
          );
        })}
      </>
    ) : null);
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {this.renderAdmins()}
          {this.renderPendingAdmins()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default ChampionshipAdminsSectionView;
